import React, { useContext, useEffect, useMemo, useState } from 'react';
import Notifications from 'components/notifications/Notifications';
import useInput from 'lib/hooks/useInput';
import useDateInput from 'lib/hooks/useDateInput';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import { useDidUpdateEffect } from 'lib/utils';
import { NotificationActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { PrivateSocketContext } from 'contexts/PrivateSocketContext';

export default function NotificationsContainer() {
  const {
    language,
    notificationsData,
    fetchNotificationsSuccess,
    fetchNotificationsFailure,
    readNotificationsSuccess,
    deleteNotificationsSuccess,
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    notificationsData: state.notification.notifications.data,
    fetchNotificationsSuccess: state.notification.notifications.success,
    fetchNotificationsFailure: state.notification.notifications.failure,
    readNotificationsSuccess: state.notification.readNotifications.success,
    deleteNotificationsSuccess: state.notification.deleteNotifications.success,
  }));
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const notificationList = notificationsData?.list;
  const pagingData = notificationsData?.pagingData;
  const page = useInput(1);
  const period = useDateInput(null);
  const keyword = useInput('');
  const checkedNotifications = useCheckSetInput(new Set([]));
  const { hasNewEvents } = useContext(PrivateSocketContext);

  let searchParams = useMemo(
    () => ({
      page: page.value,
      searchStartTime: period.value ? period.value[0].unix() : null,
      searchEndTime: period.value ? period.value[1].unix() : null,
      keyword: keyword.value || null,
    }),
    [page.value, period.value, keyword.value],
  );

  // SECTION: function
  const handleRead = () => {
    if (!checkedNotifications.value.size) return;
    NotificationActions.read_notifications_request({
      notificationIdArr: [...checkedNotifications.value],
    });
  };
  const handleDelete = () => {
    if (!checkedNotifications.value.size) return;
    NotificationActions.delete_notifications_request({
      notificationIdArr: [...checkedNotifications.value],
    });
  };
  const handleSearch = () => {
    NotificationActions.fetch_notifications_request({ ...searchParams, page: 1 });
    page.setValue(1);
    checkedNotifications.setValue(new Set([]));
  };

  const handleChangePage = (value: number) => {
    NotificationActions.fetch_notifications_request({ ...searchParams, page: value });
    page.setValue(value);
    checkedNotifications.setValue(new Set([]));
  };

  const handleRefreshList = () => {
    NotificationActions.fetch_notifications_request(searchParams);
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  // SECTION: DidMount
  useEffect(() => {
    NotificationActions.fetch_notifications_request(searchParams);
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  // SECTION: DidUpdate
  useDidUpdateEffect(() => {
    NotificationActions.fetch_notifications_request({ ...searchParams, page: 1 });
    page.setValue(1);
    checkedNotifications.setValue(new Set([]));
  }, [period.value]);

  useDidUpdateEffect(() => {
    if (readNotificationsSuccess) {
      NotificationActions.fetch_new_notifications_request({ language: language.toUpperCase() });
      checkedNotifications.setValue(new Set([]));
    }
  }, [!!readNotificationsSuccess]);

  useDidUpdateEffect(() => {
    if (deleteNotificationsSuccess) {
      NotificationActions.fetch_new_notifications_request({ language: language.toUpperCase() });
      NotificationActions.fetch_notifications_request({ ...searchParams });
      checkedNotifications.setValue(new Set([]));
    }
  }, [!!deleteNotificationsSuccess]);

  useDidUpdateEffect(() => {
    if (!fetchNotificationsSuccess) return;
    setIsLoadSuccess(true);
  }, [fetchNotificationsSuccess]);
  useDidUpdateEffect(() => {
    if (!fetchNotificationsFailure) return;
    setIsLoadFailure(true);
  }, [fetchNotificationsFailure]);

  // notification페이지에서 socket notification이 발생했을 경우 fetch요청, page1일 경우만 갱신
  useDidUpdateEffect(() => {
    if (hasNewEvents.value && page.value === 1) {
      NotificationActions.fetch_notifications_request(searchParams);
      checkedNotifications.setValue(new Set([]));
      hasNewEvents.setValue(false);
    }
  }, [hasNewEvents.value, page.value]);

  return (
    <Notifications
      notificationList={notificationList}
      isLoadSuccess={isLoadSuccess}
      isLoadFailure={isLoadFailure}
      pagingData={pagingData}
      page={page}
      period={period}
      keyword={keyword}
      checkedNotifications={checkedNotifications}
      onSearch={handleSearch}
      onRead={handleRead}
      onDelete={handleDelete}
      onChangePage={handleChangePage}
    />
  );
}
